import { BreakpointObserver } from "@angular/cdk/layout";
import { ChangeDetectionStrategy, Component, Input, OnInit, Output } from "@angular/core";
import { UntypedFormControl, UntypedFormGroup } from "@angular/forms";

import { combineLatest, Observable, Subject } from "rxjs";
import { distinctUntilChanged, map, shareReplay, startWith, switchMap } from "rxjs/operators";

import { AktuelBrugerObserver, Rolle } from "@e-forsyning/common/bruger";
import { InstallationService } from "@e-forsyning/common/installation";
import { VejService } from "@e-forsyning/common/vej";

const visteKolonnerForbruger: string[] = ["adresse", "forsyning", "maalerNr", "ejendomNr"];
const visteKolonnerForbrugerXs: string[] = ["adresse", "forsyning", "maalerNr"];
const visteKolonnerIkkeForbruger: string[] = ["adresse", "ejendomNr", "maalerNr", "maalerType"];
const visteKolonnerIkkeForbrugerXs: string[] = ["adresse", "ejendomNr", "maalerNr"];
const visteKolonner = [
    [visteKolonnerIkkeForbruger, visteKolonnerIkkeForbrugerXs],
    [visteKolonnerForbruger, visteKolonnerForbrugerXs]
];

@Component({
    selector: "installationer-soeg",
    styleUrls: ["./installationer-soeg.component.scss"],
    templateUrl: "./installationer-soeg.component.html",
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class InstallationerSoegComponent implements OnInit {
    private readonly erForbruger$ = this.brugerObserver.valueChanges.pipe(map((bruger) => Rolle.erForbruger(bruger.rolle)));
    private readonly erLilleSkaerm$ = this.breakpointObserver.observe("(min-width: 768px)").pipe(map((x) => !x.matches));
    private readonly valueChangeSubject = new Subject<string>();
    public dummyFormGroup = new UntypedFormGroup({}); // Bruges for at kunne få vist en Search-knap i iOS (i stedet for Return)

    @Input()
    public control: UntypedFormControl;

    @Output()
    public readonly valueChange = this.valueChangeSubject.pipe(distinctUntilChanged()); // new EventEmitter<string>();
    public visteKolonner$: Observable<string[]>;

    public filteredOptions$: Observable<string[]>;
    public veje$: Observable<string[]>;

    constructor(
        public readonly service: InstallationService,
        private readonly breakpointObserver: BreakpointObserver,
        private readonly brugerObserver: AktuelBrugerObserver,
        private readonly vejService: VejService
    ) {}

    public ngOnInit(): void {
        this.veje$ = this.hentVeje();
        this.visteKolonner$ = combineLatest([this.erForbruger$, this.erLilleSkaerm$]).pipe(
            map(([erForbruger, erLilleSkaerm]) => visteKolonner[+erForbruger][+erLilleSkaerm])
        );
        this.filteredOptions$ = this.control.valueChanges.pipe(
            startWith(""),
            map((value) => value || ""),
            switchMap((value) => this.veje$.pipe(map((veje) => ({ value, veje })))),
            map((data) =>
                data.veje
                    .filter((v) => v.toLocaleLowerCase().includes(data.value.toLocaleLowerCase()))
                    .sort((a, b) => this.sort(data.value, a, b))
            )
        );
    }

    public onSearchValueChange(value: string): void {
        this.valueChangeSubject.next(value);
    }

    private hentVeje(): Observable<string[]> {
        return this.vejService.hent(true).pipe(
            map((veje) =>
                veje.map((v) => {
                    const navn = v.navn || "";
                    const postNr = veje.find((vv) => vv.navn === v.navn && vv.postNr !== v.postNr) ? v.postNr : "";
                    return !!postNr ? `${navn} (${postNr})` : navn;
                })
            ),
            map((veje) => [...new Set(veje)]),
            shareReplay(1)
        );
    }

    private sort(searchValue: string, a: string, b: string): number {
        // Lowercase
        searchValue = (searchValue || "").toLowerCase();
        const valueA = (a || "").toLowerCase();
        const valueB = (b || "").toLowerCase();
        // Ved søgnig på "vej" ønsker vi at vise "Vejlevej" før "Koldingvej"
        const aIsPrioritized = valueA.startsWith(searchValue);
        const bIsPrioritized = valueB.startsWith(searchValue);
        return aIsPrioritized && !bIsPrioritized ? -1 : bIsPrioritized && !aIsPrioritized ? 1 : valueA.localeCompare(valueB);
    }
}
